import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, ExternalLink, X } from "lucide-react";
import { formatBytes } from "../api";
import { useDialogFokus } from "../lib/dialog";

export interface Anhang {
  /** Index in der Mail — darüber lädt der Server die Datei. */
  index: number;
  filename: string;
  contentType: string;
  size: number;
}

export type Darstellung = "bild" | "pdf" | "text" | "keine";

/** Was der Browser selbst zeigen kann. Alles andere bleibt ein Download. */
export function darstellungVon(a: Pick<Anhang, "filename" | "contentType">): Darstellung {
  const typ = (a.contentType || "").toLowerCase();
  const ext = a.filename.toLowerCase().split(".").pop() ?? "";
  if (/^image\/(png|jpe?g|gif|webp|avif|bmp|svg\+xml)$/.test(typ)) return "bild";
  if (typ === "application/pdf" || (ext === "pdf" && typ === "application/octet-stream")) return "pdf";
  if (/^text\/(plain|csv|calendar|vcard|x-vcard)$/.test(typ)) return "text";
  return "keine";
}

export const anhangUrl = (messageId: number, a: Anhang, ansicht = false) =>
  `/api/messages/${messageId}/attachments/${a.index}${ansicht ? "?ansicht=1" : ""}`;

/**
 * Vorschau für Bilder und Textdateien, mit Blättern über alle Anhänge.
 * PDFs öffnet der Leser direkt im Browser-Betrachter (neuer Tab): In einem
 * Rahmen blockiert sie die X-Frame-Options-Regel des Proxys.
 */
export function AnhangVorschau({ messageId, anhaenge, start, onClose }: {
  messageId: number;
  anhaenge: Anhang[];
  start: number;
  onClose: () => void;
}) {
  const [i, setI] = useState(start);
  const box = useRef<HTMLDivElement>(null);
  useDialogFokus(box, onClose);
  const a = anhaenge[i]!;
  const art = darstellungVon(a);
  const [text, setText] = useState<string | null>(null);
  const [fehler, setFehler] = useState(false);

  useEffect(() => {
    setFehler(false);
    setText(null);
    if (art !== "text") return;
    let aktiv = true;
    fetch(anhangUrl(messageId, a, true), { credentials: "same-origin" })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => aktiv && setText(t.length > 200_000 ? `${t.slice(0, 200_000)}\n\n… (gekürzt)` : t))
      .catch(() => aktiv && setFehler(true));
    return () => { aktiv = false; };
  }, [messageId, a, art]);

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") { e.preventDefault(); setI((n) => (n - 1 + anhaenge.length) % anhaenge.length); }
      if (e.key === "ArrowRight") { e.preventDefault(); setI((n) => (n + 1) % anhaenge.length); }
    };
    document.addEventListener("keydown", taste);
    return () => document.removeEventListener("keydown", taste);
  }, [anhaenge.length]);

  const mehrere = anhaenge.length > 1;

  return (
    <div className="vorschau-hintergrund" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="vorschau" ref={box} role="dialog" aria-modal="true" aria-label={`Vorschau: ${a.filename}`}>
        <header className="vorschau-kopf">
          <span className="vorschau-name" title={a.filename}>{a.filename || "(ohne Namen)"}</span>
          <span className="vorschau-info">
            {formatBytes(a.size)}{mehrere ? ` · ${i + 1} von ${anhaenge.length}` : ""}
          </span>
          <span className="spacer" />
          {art === "pdf" && (
            <a className="btn quiet" href={anhangUrl(messageId, a, true)} target="_blank" rel="noopener">
              <ExternalLink aria-hidden="true" /> <span className="nur-breit">Im Browser öffnen</span>
            </a>
          )}
          <a className="btn" href={anhangUrl(messageId, a)} download={a.filename}>
            <Download aria-hidden="true" /> <span className="nur-breit">Herunterladen</span>
          </a>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Vorschau schließen" title="Schließen (Esc)">
            <X aria-hidden="true" />
          </button>
        </header>

        <div className="vorschau-flaeche">
          {mehrere && (
            <button type="button" className="vorschau-blaettern" data-seite="links" aria-label="Vorheriger Anhang"
              onClick={() => setI((n) => (n - 1 + anhaenge.length) % anhaenge.length)}>
              <ChevronLeft aria-hidden="true" />
            </button>
          )}
          {fehler ? (
            <div className="vorschau-karte"><b>Vorschau nicht möglich</b><span>Die Datei lässt sich trotzdem herunterladen.</span></div>
          ) : art === "bild" ? (
            <img key={a.index} className="vorschau-bild" src={anhangUrl(messageId, a, true)} alt={a.filename} onError={() => setFehler(true)} />
          ) : art === "text" ? (
            text === null ? <span className="kreisel" aria-label="Lädt" /> : <pre className="vorschau-text">{text}</pre>
          ) : (
            <div className="vorschau-karte">
              <b>{art === "pdf" ? "PDF-Dokument" : "Keine Vorschau für diesen Dateityp"}</b>
              <span>{art === "pdf" ? "Öffnet im PDF-Betrachter deines Browsers." : "Zum Ansehen herunterladen."}</span>
              {art === "pdf" && (
                <a className="btn primary" href={anhangUrl(messageId, a, true)} target="_blank" rel="noopener">
                  <ExternalLink aria-hidden="true" /> Im Browser öffnen
                </a>
              )}
            </div>
          )}
          {mehrere && (
            <button type="button" className="vorschau-blaettern" data-seite="rechts" aria-label="Nächster Anhang"
              onClick={() => setI((n) => (n + 1) % anhaenge.length)}>
              <ChevronRight aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
